import { ConfigProvider, Tooltip } from "antd";
import enUSIntl from "antd/lib/locale/en_US";
import { FC, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectBasket,
  setBasket,
} from "src/store/Goal-Based/SelfMadeSmartfolioSlice";
import { BasketItem, Security } from "src/store/Goal-Based/goal";
import { useGetSecuritiesQuery } from "src/store/Goal-Based/goalBasedApi";
import { noWrapTextRender } from "../../../TextWrapper";
import type { FilterConfirmProps } from "antd/es/table/interface";
import {
  Cancel01Icon,
  FilterIcon,
  Search01Icon,
} from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import styled from "styled-components";
import { colors } from "src/theme/colors";
import { line, radii, withAlpha } from "src/theme/ui";
import { ProTableTheme } from "src/components/ProTable";
import { Badge, Button as UIButton } from "src/components/ui";

type DataIndex =
  | "symbol"
  | "security"
  | "type"
  | "sector"
  | "market"
  | "exchange"
  | "currency";

/**
 * What has been picked so far.
 *
 * A row of Ant Design tags that WRAPPED. Ticking a dozen securities grew it
 * into four lines, which pushed the table down inside a fixed dialog and took
 * four rows off the list you were still choosing from — the selection ate the
 * thing it was being made in. It also said nothing about why Next was
 * disabled, and gave no way to undo a tick except finding the row again in a
 * table of hundreds.
 *
 * So: one line, always. The count leads, because that is what the rule is
 * about; the chips scroll sideways after that; and each one is a button that
 * takes its security back off the list. Nothing here submits anything.
 */
const Selection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  width: 100%;
`;

const SelectionCount = styled.span`
  flex: none;
  font-size: 11.5px;
  font-weight: 600;
  color: ${colors.gray[700]};
`;

const Rule = styled.span`
  flex: none;
  font-size: 11.5px;
  color: ${colors.gray[500]};
`;

/* Sideways, with the scrollbar out of the way — this is a strip of what you
   have, not a list to read down. */
const Chips = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
  padding-bottom: 1px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex: none;
  height: 24px;
  padding: 0 6px 0 9px;
  border: 1px solid ${withAlpha(colors.primary[500], 0.22)};
  border-radius: ${radii.pill}px;
  background: ${withAlpha(colors.primary[500], 0.07)};
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 600;
  color: ${colors.primary[600]};
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${withAlpha(colors.primary[500], 0.13)};
    border-color: ${withAlpha(colors.primary[500], 0.35)};
  }

  > svg {
    opacity: 0.65;
  }
`;

/* The per-column search, in the product's field and buttons rather than Ant
   Design's blue primary and its 90px fixed halves. */
const FilterPanel = styled.div`
  width: 216px;
  padding: 10px;
`;

const FilterField = styled.input`
  width: 100%;
  height: 32px;
  margin-bottom: 8px;
  padding: 0 10px;
  border: 1px solid ${line.field};
  border-radius: ${radii.control}px;
  background: ${colors.white};
  font-family: inherit;
  font-size: 12.5px;
  color: ${colors.gray[900]};
  outline: none;

  &::placeholder {
    color: ${colors.gray[400]};
  }

  &:focus {
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px ${withAlpha(colors.primary[500], 0.14)};
  }
`;

const FilterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
`;

export interface ISecuritiesTable {
  setSecurities: React.Dispatch<React.SetStateAction<any[]>>;
  securities: any[];
}

export const SecuritiesTable: FC = () => {
  const dispatch = useDispatch<any>();
  const basket = useSelector(selectBasket);
  const { data, isLoading } = useGetSecuritiesQuery();
  const searchInput = useRef<any>(null);

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex,
  ) => {
    confirm();
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
  };

  const rowSelection = {
    selectedRowKeys: basket.slice(1).map(({ ticker_id }) => ticker_id),
    onChange: (selectedRowKeys, selectedRows) => {
      dispatch(
        setBasket(
          selectedRows.map((item: Security) => {
            const basketItem: BasketItem = {
              minimum_bound_weight: 0,
              maximum_bound_weight: 1,
              weight: 0,
              ticker_id: item.key,
              name: item.security,
              symbol: item.symbol,
            };
            return basketItem;
          }),
        ),
      );
    },
  };

  /* Untick one, from the strip rather than from the table. The basket's first
     entry is cash and is not a security the reader chose, so it is never
     shown and never dropped. */
  const unpick = (tickerId: number) =>
    dispatch(
      setBasket(basket.slice(1).filter((item) => item.ticker_id !== tickerId)),
    );

  const tableAlertRender = () => {
    const picked = basket.slice(1);
    return (
      <Selection>
        <SelectionCount>{picked.length} selected</SelectionCount>
        {picked.length < 2 && <Rule>Pick at least 2 to continue</Rule>}
        <Chips>
          {picked.map((item) => (
            <Chip
              key={item.ticker_id}
              type="button"
              title={`Remove ${item.symbol}`}
              onClick={() => unpick(item.ticker_id)}
            >
              {item.symbol}
              <HugeIcon icon={Cancel01Icon} size={11} strokeWidth={2.4} />
            </Chip>
          ))}
        </Chips>
      </Selection>
    );
  };

  const getColumnSearchProps = (dataIndex: DataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <FilterPanel onKeyDown={(e) => e.stopPropagation()}>
        <FilterField
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0] ?? ""}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onKeyDown={(e) => {
            if (e.key === "Enter")
              handleSearch(selectedKeys as string[], confirm, dataIndex);
          }}
        />
        <FilterActions>
          <UIButton
            variant="ghost"
            size="xs"
            onClick={() => clearFilters && handleReset(clearFilters)}
          >
            Reset
          </UIButton>
          <UIButton
            variant="primary"
            size="xs"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
          >
            Search
          </UIButton>
        </FilterActions>
      </FilterPanel>
    ),
    filterIcon: (filtered: boolean) => (
      <HugeIcon
        icon={Search01Icon}
        style={{ color: filtered ? "#1677ff" : undefined }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownVisibleChange: (visible) =>
      visible && setTimeout(() => searchInput.current.focus(), 100),
  });

  const columns = [
    {
      title: noWrapTextRender("Symbol"),
      sorter: (a, b) => a?.symbol.localeCompare(b?.symbol),
      key: "symbol",
      dataIndex: "symbol",
      width: 115,
      render: (symbol) => <strong>{symbol}</strong>,
      ...getColumnSearchProps("symbol"),
    },
    {
      title: noWrapTextRender("Security"),
      sorter: (a, b) => a?.security.localeCompare(b?.security),
      key: "security",
      dataIndex: "security",
      ...getColumnSearchProps("security"),
      /* Wide enough to read. It was clipped at 10 CHARACTERS behind a
         tooltip, so the column that names the thing being chosen showed
         "ULTRASHO…" and "Consumer …" — and every row needed a hover to
         answer the question the column exists to answer. The tooltip stays
         for the few names that still run past it. */
      width: 260,
      render: (security) => (
        <Tooltip title={security}>
          <p
            style={{
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
              margin: 0,
            }}
          >
            {security}
          </p>
        </Tooltip>
      ),
    },
    {
      title: noWrapTextRender("Type"),
      key: "type",
      dataIndex: "type",
      filterSearch: true,
      filters: [
        { text: "ETF", value: "ETF" },
        { text: "Stock", value: "Stock" },
        { text: "Composite_benchmark", value: "Composite_benchmark" },
      ],
      filterIcon: () => <HugeIcon icon={FilterIcon} />,
      onFilter: (value: string, record) =>
        (record?.type as string).toLowerCase() === value.toLowerCase(),
      width: 110,
      /* The product's badge, not Ant Design's tag: "ETF" and "Stock" are
         states of a row, and every other state in the app is a badge. */
      render: (type) => (
        <Badge tone={type === "ETF" ? "info" : "gray"}>{type}</Badge>
      ),
    },
    {
      title: noWrapTextRender("Sector"),
      key: "sector",
      dataIndex: "sector",
      render: (sector) => sector.join(", "),
      filterSearch: true,
      filters: [
        { text: "Energy", value: "Energy" },
        { text: "Information Technology", value: "Information Technology" },
        { text: "Materials", value: "Materials" },
        { text: "Consumer Discretionary", value: "Consumer Discretionary" },
        { text: "Health Care", value: "Health Care" },
        { text: "Communication Services", value: "Communication Services" },
        { text: "Financials", value: "Financials" },
        { text: "Real Estate", value: "Real Estate" },
        { text: "Consumer Staples", value: "Consumer Staples" },
        { text: "Others", value: "Others" },
      ],
      filterIcon: () => <HugeIcon icon={FilterIcon} />,
      onFilter: (value: string, record) =>
        (record?.sector as string[]).some(
          (item) => item.toLowerCase() === value.toLowerCase(),
        ),
    },
    {
      title: noWrapTextRender("Market"),
      key: "market",
      dataIndex: "market",
      width: 100,
      filterSearch: true,
      filters: [
        { text: "SW", value: "SW" },
        { text: "US", value: "US" },
        { text: "LN", value: "LN" },
      ],
      filterIcon: () => <HugeIcon icon={FilterIcon} />,
      onFilter: (value: string, record) =>
        (record?.market as string).toLowerCase() === value.toLowerCase(),
    },
    {
      title: noWrapTextRender("Exchange"),
      key: "exchange",
      dataIndex: "exchange",
      width: 130,
      filterSearch: true,
      filters: [
        { text: "NYSE", value: "NYSE" },
        { text: "NASDAQ", value: "NASDAQ" },
        { text: "LSE", value: "LSE" },
        { text: "SWX", value: "SWX" },
        { text: "NYSE Arca", value: "NYSE Arca" },
      ],
      filterIcon: () => <HugeIcon icon={FilterIcon} />,
      onFilter: (value: string, record) =>
        (record?.exchange as string).toLowerCase() === value.toLowerCase(),
    },
    {
      title: noWrapTextRender("Currency"),
      key: "currency",
      dataIndex: "currency",
      width: 110,
    },
  ];

  return (
    <ConfigProvider locale={enUSIntl}>
      <ProTableTheme
        loading={isLoading}
        /* No cell grid. The frame and the header band separate the table
           from the dialog; a rule around every cell of eight columns turns a
           list into graph paper. */
        bordered={false}
        search={false}
        /* No reload, density or column-chooser glyphs. Three unlabelled
           icons whose meaning has to be learned from a tooltip, in a dialog
           whose only job is picking rows — the same three that came off the
           dashboard's table. */
        options={false}
        pagination={false}
        columns={columns}
        dataSource={data?.filter((item) => item.symbol !== "CASH")}
        tableAlertRender={tableAlertRender}
        tableAlertOptionRender={({ onCleanSelected }) => null}
        scroll={{ y: 400, x: "max-content" }}
        rowSelection={{
          type: "checkbox",
          hideSelectAll: true,
          ...rowSelection,
        }}
      />
    </ConfigProvider>
  );
};
